class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.specializedUpsellsTemplate = this.querySelector('.bbs-cart-upsells')?.cloneNode(true) || null;
    this.syncDrawerTopOffset = this.syncDrawerTopOffset.bind(this);

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.addEventListener('click', (event) => {
      const upsells = event.target.closest('.bbs-cart-upsells');
      const upsellsToggle = event.target.closest('[data-bbs-upsells-toggle]');
      const collapsedUpsellList = event.target.closest('.bbs-cart-upsells__list');

      if (
        upsells &&
        (upsellsToggle ||
          (collapsedUpsellList &&
            window.matchMedia('(max-width: 749px)').matches &&
            !upsells.classList.contains('is-expanded')))
      ) {
        event.preventDefault();
        this.toggleSpecializedUpsells(upsells);
        return;
      }

      const upsellButton = event.target.closest('.bbs-cart-upsell__button[data-variant-id]');
      if (!upsellButton || upsellButton.disabled) return;
      event.preventDefault();
      this.addSpecializedPlan(upsellButton);
    });
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  toggleSpecializedUpsells(upsells) {
    const isExpanded = upsells.classList.toggle('is-expanded');
    const toggle = upsells.querySelector('[data-bbs-upsells-toggle]');
    const label = toggle?.querySelector('.bbs-cart-upsells__toggle-label');

    if (toggle) toggle.setAttribute('aria-expanded', String(isExpanded));
    if (label) label.textContent = isExpanded ? 'Show less' : 'Show more';
  }

  syncDrawerTopOffset() {
    const announcementBar = document.querySelector('#bundle-smart_bar, .announcement-bar-section');
    let topOffset = 0;

    if (announcementBar) {
      const announcementStyles = window.getComputedStyle(announcementBar);
      const announcementBounds = announcementBar.getBoundingClientRect();
      const isVisible =
        announcementStyles.display !== 'none' &&
        announcementStyles.visibility !== 'hidden' &&
        announcementBounds.height > 0 &&
        announcementBounds.bottom > 0;

      if (isVisible) topOffset = Math.ceil(announcementBounds.bottom);
    }

    this.style.setProperty('--bbs-cart-drawer-top-offset', `${topOffset}px`);
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    this.syncDrawerTopOffset();
    window.addEventListener('resize', this.syncDrawerTopOffset);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
    document.body.classList.add('bbs-cart-drawer-open');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    window.removeEventListener('resize', this.syncDrawerTopOffset);
    document.body.classList.remove('overflow-hidden');
    document.body.classList.remove('bbs-cart-drawer-open');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  async addSpecializedPlan(button) {
    const status = this.querySelector('.bbs-cart-upsells__status');
    const variantId = button.dataset.variantId;
    const originalLabel = button.textContent;

    button.disabled = true;
    button.classList.add('is-loading');
    if (status) status.textContent = '';
    this.setActiveElement(button);

    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', '1');
    formData.append(
      'sections',
      this.getSectionsToRender().map((section) => section.id)
    );
    formData.append('sections_url', window.location.pathname);
    config.body = formData;

    try {
      const response = await fetch(`${routes.cart_add_url}`, config);
      const parsedState = await response.json();

      if (!response.ok || parsedState.status) {
        throw new Error(parsedState.description || parsedState.message || 'This plan could not be added.');
      }

      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'specialized-cart-upsell',
        productVariantId: variantId,
        cartData: parsedState,
      });
      this.renderContents(parsedState);
    } catch (error) {
      button.disabled = false;
      button.classList.remove('is-loading');
      button.textContent = originalLabel;
      if (status) status.textContent = error.message;
    }
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    this.restoreSpecializedUpsells(parsedState);

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  restoreSpecializedUpsells(cartState) {
    const drawer = this.querySelector('#CartDrawer');
    if (!drawer) return;

    let upsells = drawer.querySelector('.bbs-cart-upsells');
    if (!upsells && this.specializedUpsellsTemplate) {
      const header = drawer.querySelector('.drawer__header');
      if (header) {
        upsells = this.specializedUpsellsTemplate.cloneNode(true);
        header.before(upsells);
      }
    }

    if (!upsells) return;

    upsells.hidden = cartState.item_count === 0;
    upsells.classList.remove('is-expanded');
    const upsellsToggle = upsells.querySelector('[data-bbs-upsells-toggle]');
    const upsellsToggleLabel = upsellsToggle?.querySelector('.bbs-cart-upsells__toggle-label');
    if (upsellsToggle) upsellsToggle.setAttribute('aria-expanded', 'false');
    if (upsellsToggleLabel) upsellsToggleLabel.textContent = 'Show more';
    const cartVariantIds = new Set((cartState.items || []).map((item) => String(item.variant_id || item.id)));

    upsells.querySelectorAll('.bbs-cart-upsell__button[data-variant-id]').forEach((button) => {
      const isInCart = cartVariantIds.has(String(button.dataset.variantId));
      button.disabled = isInCart;
      button.classList.toggle('is-in-cart', isInCart);
      button.classList.remove('is-loading');
      button.textContent = isInCart ? 'In cart' : 'Add to cart';
    });

    const status = upsells.querySelector('.bbs-cart-upsells__status');
    if (status) status.textContent = '';
    this.specializedUpsellsTemplate = upsells.cloneNode(true);
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
